/**
 * Custom Options Handler
 * Manages custom options UI and syncs with variant changes
 * 
 * Available methods for jQuery/external use:
 * - handleVariantChange(variant) - Call when variant changes
 * - updateCustomOptionsFromVariant(variant) - Update options from variant
 * - updateCustomOptionsPrice(variant) - Update price display
 * - findMatchingVariant() - Find variant matching selected options
 * - updateProductImage() - Update product image based on selected options
 */


class CustomOptionsHandler {
  constructor(container) {
    this.container = container;
    this.productId = container.dataset.productId;
    this.sectionId = container.closest('[data-section]')?.dataset.section || '';
    this.selectedOptions = {};
    this.isUpdatingFromVariant = false; // Flag to prevent infinite loops
    this.isSyncingPaintColor = false; // Flag to prevent infinite loops when syncing paint/color
    this.selectedVariantPrice = null; // Store the current selected variant price
    this.selectedVariantRawPrice = null; // Store the raw numeric price

    // Ensure productId is a string for comparison
    if (this.productId) {
      this.productId = String(this.productId);
    }

    // Get variant mapping data - try product ID first, then fall back to section ID
    let variantMappingScript = null;
    if (this.productId) {
      variantMappingScript = document.getElementById(`custom-options-variant-mapping-${this.productId}`);
    }
    // Fallback to section ID for backwards compatibility
    if (!variantMappingScript && this.sectionId) {
      variantMappingScript = document.getElementById(`custom-options-variant-mapping-${this.sectionId}`);
    }

    if (variantMappingScript) {
      try {
        const jsonText = variantMappingScript.textContent.trim();

        // Log the JSON text for debugging (first 500 chars)        
        this.variantMapping = JSON.parse(jsonText);
      } catch (e) {
        console.error('Error parsing variant mapping:', e);
        console.error('Error message:', e.message);

        // Try to find the problematic line
        if (e.message && e.message.includes('line')) {
          const jsonText = variantMappingScript.textContent.trim();
          const lines = jsonText.split('\n');
          const lineMatch = e.message.match(/line (\d+)/);
          if (lineMatch) {
            const lineNum = parseInt(lineMatch[1]) - 1;
            console.error('Problematic line:', lineNum + 1, ':', lines[lineNum]);
            console.error('Context (3 lines before and after):');
            for (let i = Math.max(0, lineNum - 3); i < Math.min(lines.length, lineNum + 4); i++) {
              console.error(`${i + 1}:`, lines[i]);
            }
          }
        }

        // Show the full JSON for debugging
        console.error('Full JSON text:', variantMappingScript.textContent);

        this.variantMapping = { variants: [] };
      }
    } else {
      console.warn('Variant mapping script not found for product ID:', this.productId, 'or section ID:', this.sectionId);
      this.variantMapping = { variants: [] };
    }

    this.init();
  }

  init() {
    // Set default selected options (first option in each group) and add variant IDs
    this.container.querySelectorAll('.custom-option-group').forEach(group => {
      const firstOption = group.querySelector('.custom-option');
      if (firstOption) {
        const optionGroup = group.dataset.optionGroup;
        const optionName = firstOption.dataset.optionName;
        const isMultipleSelection = group.dataset.multipleSelection === 'true';

        if (isMultipleSelection) {
          // Initialize as array for multiple selection
          this.selectedOptions[optionGroup] = [optionName];
        } else {
          // Initialize as single value for radio behavior
          this.selectedOptions[optionGroup] = optionName;
        }
      }
    });

    // Add click event listeners
    this.container.querySelectorAll('.custom-option').forEach(option => {
      option.addEventListener('click', (e) => this.handleOptionClick(e));
    });

    // Check and update image visibility based on color name
    this.checkAndUpdateImageVisibility();

    // Update initial image if needed
    this.updateProductImage();

    // Update initial price if variant is available
    const initialVariant = this.findMatchingVariant();
    if (initialVariant) {
      this.updateCustomOptionsPrice(initialVariant);
    } else {
      // Initialize price variables even if no variant found
      this.selectedVariantPrice = null;
      this.selectedVariantRawPrice = null;
    }

    // Update initial size classes on page load
    this.updateSizeClasses();
    
    // Initialize selection counters for multiple selection groups
    const multipleSelectionGroups = this.container.querySelectorAll('.custom-option-group[data-multiple-selection="true"]');
    multipleSelectionGroups.forEach(group => {
      this.updateSelectionCounter(group);
    });

    // Update initial neck classes on page load
    this.updateNeckClasses();

    // Trigger clicks on all initially selected options to initialize everything
    this.container.querySelectorAll('.custom-option.is-selected').forEach(option => {
      option.click();
    });
  }

  /**
   * Update custom options UI based on selected variant from mapping
   * Call this to update options when variant changes
   * @param {Object} mappedVariant - Variant object from mapping with id, options, featured_media_id
   */
  updateCustomOptionsFromVariant(mappedVariant) {
    if (!mappedVariant || !mappedVariant.options) {
      console.warn('updateCustomOptionsFromVariant: No variant or options provided', mappedVariant);
      return;
    }

    // Get all custom option groups
    const optionGroups = Array.from(this.container.querySelectorAll('.custom-option-group'));

    // Update each custom option group based on variant options from mapping
    optionGroups.forEach(group => {
      const optionGroup = group.dataset.optionGroup;

      // Find the custom option that matches any of the variant's options
      let matchedOption = null;

      // Check all custom options in this group
      const optionsInGroup = Array.from(group.querySelectorAll('.custom-option'));

      for (const option of optionsInGroup) {
        const optionName = option.dataset.optionName;

        if (!optionName) {
          continue;
        }

        // Check if this option matches any of the variant's options from mapping
        const normalizedOptionName = optionName.toLowerCase().trim();

        for (const variantOption of mappedVariant.options) {
          const normalizedVariantOption = String(variantOption).toLowerCase().trim();

          if (normalizedOptionName === normalizedVariantOption) {
            matchedOption = option;
            break;
          }
        }

        if (matchedOption) {
          break;
        }
      }

      // If we found a matching option, select it
      if (matchedOption) {
        // Remove selected state from all options in this group
        group.querySelectorAll('.custom-option').forEach(opt => {
          opt.classList.remove('is-selected');
        });

        // Add selected state to matched option
        matchedOption.classList.add('is-selected');

        // Add data-variant-id to the option if variant has an id
        if (mappedVariant.id) {
          matchedOption.setAttribute('data-variant-id', mappedVariant.id);
        }

        // Update selected options
        this.selectedOptions[optionGroup] = matchedOption.dataset.optionName;
      } else {
        console.warn('updateCustomOptionsFromVariant: No matching option found for group', optionGroup);
      }
    });

    // Update size classes based on selected size
    this.updateSizeClasses();

    // Update neck classes based on selected neck option
    this.updateNeckClasses();

    // Check and update image visibility based on color name
    this.checkAndUpdateImageVisibility();

    // Fallback: try to find matching variant and update image
    this.updateProductImage();

    // Update price after updating options
    this.updateCustomOptionsPrice(mappedVariant);
  }

  handleOptionClick(event) {
    const option = event.currentTarget;
    const optionGroup = option.dataset.optionGroup;
    const group = option.closest('.custom-option-group');
    const isMultipleSelection = group.dataset.multipleSelection === 'true';

    if (isMultipleSelection) {
      // Checkbox behavior: toggle selection
      const isCurrentlySelected = option.classList.contains('is-selected');

      if (isCurrentlySelected) {
        // Unselect the option
        option.classList.remove('is-selected');
      } else {
        // Select the option
        option.classList.add('is-selected');
      }

      // Update selected options array for multiple selection
      if (!this.selectedOptions[optionGroup]) {
        this.selectedOptions[optionGroup] = [];
      }

      const optionName = option.dataset.optionName;
      const selectedArray = this.selectedOptions[optionGroup];

      if (isCurrentlySelected) {
        // Remove from array
        const index = selectedArray.indexOf(optionName);
        if (index > -1) {
          selectedArray.splice(index, 1);
        }
      } else {
        // Add to array if not already present
        if (!selectedArray.includes(optionName)) {
          selectedArray.push(optionName);
        }
      }
    } else {
      // Radio behavior: single selection (original behavior)
      // Remove selected state from all options in this group
      group.querySelectorAll('.custom-option').forEach(opt => {
        opt.classList.remove('is-selected');
      });

      // Add selected state to clicked option
      option.classList.add('is-selected');

      // Update selected options
      this.selectedOptions[optionGroup] = option.dataset.optionName;
    }

    // Check if this is a paint/color option in main-product-options and sync to custom-product-options
    // Only sync if we're not already syncing (to prevent infinite loops)
    const normalizedGroup = optionGroup.toLowerCase();
    if (!this.isSyncingPaintColor) {
      const isMainProductOptions = this.container.closest('.main-product-options');
      if (isMainProductOptions) {
        const optionName = option.dataset.optionName;
        if (normalizedGroup === 'paint' || normalizedGroup === 'color' || normalizedGroup === 'colour') {
          this.syncPaintColorToCustomOptions(optionName);
        }
      }
    }

    // Update size classes based on selected size (only for single selection)
    if (!isMultipleSelection) {
      this.updateSizeClasses();

      // Update neck classes based on selected neck option (only for single selection)
      this.updateNeckClasses();
    }

    // For multiple selection, we need to calculate price from all selected options
    if (isMultipleSelection) {
      // Trigger price update for all selected options in this group
      this.updatePriceForMultipleSelection(group);
    } else {
      // Find matching variant and update data-variant-id (single selection)
      const matchingVariant = this.findMatchingVariant();
      if (matchingVariant && matchingVariant.id) {
        // Add data-variant-id to the selected option
        option.setAttribute('data-variant-id', matchingVariant.id);

        // Update price display
        this.updateCustomOptionsPrice(matchingVariant);
      }
    }

    // If color/paint option changed, update all deck length prices
    if (normalizedGroup === 'paint' || normalizedGroup === 'color' || normalizedGroup === 'colour') {
      this.updateAllDeckLengthPrices();
    }

    // Check if payment-method option is clicked and update visibility
    if (normalizedGroup === 'payment-method' || normalizedGroup === 'payment_type' || normalizedGroup === 'payment-type') {
      updatePaymentTypeVisibility();
    }

    // Update the specific image-change element based on the clicked option
    this.updateImageChangeForOption(optionGroup);

    // If paint/color is clicked, also trigger deck-length update
    if (normalizedGroup === 'paint' || normalizedGroup === 'color' || normalizedGroup === 'colour') {
      // Check and update image visibility based on color name
      this.checkAndUpdateImageVisibility();

      // Find all selected deck-length options in custom-product-options and trigger their clicks
      const selectedDeckLengthOptions = document.querySelectorAll('.custom-product-options .custom-option-group[data-option-group="deck-length"] .custom-option.is-selected');
      selectedDeckLengthOptions.forEach(option => {
        option.click();
      });


      this.updateImageChangeForOption('deck-length');
    }

    // Find the selected deck-length option and trigger its click
    const deckLengthGroup = this.container.closest('.main-product-options')?.querySelector('.custom-option-group[data-option-group="deck-length"]');

    if (deckLengthGroup != null) {
      const variantId = deckLengthGroup.querySelector('.custom-option.is-selected').getAttribute('data-option-id');
      const selectedDeckLengthOptions = document.querySelectorAll('.custom-product-options .custom-option-group .custom-option[data-option-id="' + variantId + '"]');
      selectedDeckLengthOptions.forEach(option => {
        option.click();
      });
      const other_options = document.querySelectorAll('.custom-product-options .custom-option-group .custom-option.is-selected');
      other_options.forEach(option => {
        option.click();
      });
    }

  }

  /**
   * Check if Two-Tone custom is selected and update image visibility
   */
  checkAndUpdateImageVisibility() {
    // Get selected paint/color option
    const paintGroup = this.container.querySelector('.custom-option-group[data-option-group="paint"], .custom-option-group[data-option-group="color"], .custom-option-group[data-option-group="colour"]');
    if (!paintGroup) return;

    const selectedOption = paintGroup.querySelector('.custom-option.is-selected');
    if (!selectedOption) return;

    const colorName = selectedOption.dataset.optionName || '';
    const isTwoToneCustom = colorName.toLowerCase().includes('two-tone custom') || colorName.toLowerCase().includes('two tone custom');

    const mainImg = document.querySelector('.main_img');
    const imageChangeElements = document.querySelectorAll('.image-change');
    if (isTwoToneCustom) {
      // Show main_img, hide all image-change
      if (mainImg) mainImg.style.opacity = 1;
      imageChangeElements.forEach(img => img.style.opacity = 0);
    } else {
      // Hide main_img, show all image-change
      if (mainImg) mainImg.style.opacity = 0;
      imageChangeElements.forEach(img => img.style.opacity = 1);
    }
  }

  /**
   * Update image-change element for a specific option type (length, neck, or wheel)
   */
  updateImageChangeForOption(optionGroup) {
    let normalizedGroup = optionGroup.toLowerCase();

    // Only update if it's length, ne
    if (normalizedGroup === 'length' || normalizedGroup === 'deck-length' || normalizedGroup === 'neck' || normalizedGroup === 'wheels' || normalizedGroup === 'wheel') {
      let variant = this.findVariantForOption(normalizedGroup);
      if (normalizedGroup === 'deck-length') {
        normalizedGroup = 'length';
      }
      if (normalizedGroup === 'wheels') {
        normalizedGroup = 'wheel';
      }

      if (variant) {
        // Check for featured_media (image URL)
        const imageUrl = variant.featured_media;
        if (imageUrl) {
          if (normalizedGroup === 'deck-length' || normalizedGroup === 'length') {
            const isMainProductOptions = this.container.closest('.main-product-options');
            if (isMainProductOptions) {
              const imageElement = document.querySelector(`.image-change.${normalizedGroup}`);
              if (imageElement) {
                imageElement.src = imageUrl;
              } else {
                console.warn(`updateImageChangeForOption: Image element .image-change.${normalizedGroup} not found`);
              }
            }
          } else {
            const imageElement = document.querySelector(`.image-change.${normalizedGroup}`);
            if (imageElement) {
              imageElement.src = imageUrl;
            } else {
              console.warn(`updateImageChangeForOption: Image element .image-change.${normalizedGroup} not found`);
            }
          }
        } else {
          console.warn(`updateImageChangeForOption: Variant found but no featured_media for ${normalizedGroup}`, variant);
        }
      } else {
        console.warn(`updateImageChangeForOption: No variant found for ${normalizedGroup}`, this.selectedOptions);
      }
    }
  }

  /**
   * Sync paint/color selection from main-product-options to custom-product-options
   */
  syncPaintColorToCustomOptions(selectedPaintColor) {
    if (!selectedPaintColor) {
      return;
    }

    // Set flag to prevent infinite loops
    this.isSyncingPaintColor = true;

    // Find all custom-product-options containers
    const customProductOptionsContainers = document.querySelectorAll('.custom-product-options .custom-options-wrapper');

    customProductOptionsContainers.forEach(customContainer => {
      // Find paint/color option groups in custom-product-options
      const paintColorGroups = customContainer.querySelectorAll('.custom-option-group[data-option-group="paint"], .custom-option-group[data-option-group="color"], .custom-option-group[data-option-group="colour"]');

      paintColorGroups.forEach(group => {
        // Find the option that matches the selected paint/color name
        const options = group.querySelectorAll('.custom-option');
        let matchedOption = null;

        options.forEach(opt => {
          const optionName = opt.dataset.optionName;
          if (optionName) {
            // Normalize both names for comparison (case-insensitive)
            const normalizedSelected = selectedPaintColor.toLowerCase().trim();
            const normalizedOption = optionName.toLowerCase().trim();

            // Check for exact match or if option name contains the selected name
            if (normalizedOption === normalizedSelected ||
              normalizedOption.includes(normalizedSelected) ||
              normalizedSelected.includes(normalizedOption)) {
              matchedOption = opt;
            }
          }
        });

        // If we found a matching option, select it
        if (matchedOption && !matchedOption.classList.contains('is-selected')) {
          // Get the handler for this container if it exists
          const containerId = customContainer.dataset.productId;
          const handler = window.customOptionsHandlers && window.customOptionsHandlers[containerId];

          // Remove selected state from all options in this group
          group.querySelectorAll('.custom-option').forEach(opt => {
            opt.classList.remove('is-selected');
          });

          // Add selected state to matched option
          matchedOption.classList.add('is-selected');

          // Update handler's selectedOptions if handler exists
          const optionGroup = group.dataset.optionGroup;
          if (handler && optionGroup) {
            // Set flag on handler to prevent it from syncing back
            handler.isSyncingPaintColor = true;
            handler.selectedOptions[optionGroup] = matchedOption.dataset.optionName;

            // Update variant, price, and image for the custom options handler
            const matchingVariant = handler.findMatchingVariant();
            if (matchingVariant && matchingVariant.id) {
              matchedOption.setAttribute('data-variant-id', matchingVariant.id);
              handler.updateCustomOptionsPrice(matchingVariant);
            }
            // Check and update image visibility based on color name
            handler.checkAndUpdateImageVisibility();
            handler.updateProductImage();
            handler.updateSizeClasses();
            handler.updateNeckClasses();

            // Reset flag after a short delay
            setTimeout(() => {
              handler.isSyncingPaintColor = false;
            }, 100);
          } else {
            // Fallback: trigger click event if handler not found
            matchedOption.click();
          }
        }
      });
    });

    // Reset flag after a short delay
    setTimeout(() => {
      this.isSyncingPaintColor = false;
    }, 100);
  }

  /**
   * Update classes based on selected size (14, 16, 18, 20, 22, 24, 26, 28, 30)
   */
  updateSizeClasses() {
    // Find the main product media element
    const mainProductMedia = document.querySelector('.main-product__media');
    if (!mainProductMedia) {
      return;
    }

    // Check all option groups to find the selected size
    const allOptionGroups = this.container.querySelectorAll('.custom-option-group');
    let foundSize = null;

    for (const group of allOptionGroups) {
      const selectedOption = group.querySelector('.custom-option.is-selected');
      if (selectedOption && selectedOption.dataset.optionName) {
        const optionName = selectedOption.dataset.optionName;
        // Extract numeric value from option name (handles "14", "14ft", "18", "18ft", etc.)
        const numericMatch = optionName.match(/\b(14|16|18|20|22|24|26|28|30)\b/);
        if (numericMatch) {
          foundSize = numericMatch[1];
          break; // Found the size, no need to check other groups
        }
      }
    }

    // Only remove and add classes if we found a size
    if (foundSize) {
      // Remove all size classes first
      mainProductMedia.classList.remove('size-14', 'size-16', 'size-18', 'size-20', 'size-22', 'size-24', 'size-26', 'size-28', 'size-30', 'has-size-14', 'has-size-16', 'has-size-18', 'has-size-20', 'has-size-22', 'has-size-24', 'has-size-26', 'has-size-28', 'has-size-30');

      // Add the appropriate class for the found size
      if (foundSize === '14') {
        mainProductMedia.classList.add('size-14', 'has-size-14');
      } else if (foundSize === '16') {
        mainProductMedia.classList.add('size-16', 'has-size-16');
      } else if (foundSize === '18') {
        mainProductMedia.classList.add('size-18', 'has-size-18');
      } else if (foundSize === '20') {
        mainProductMedia.classList.add('size-20', 'has-size-20');
      } else if (foundSize === '22') {
        mainProductMedia.classList.add('size-22', 'has-size-22');
      } else if (foundSize === '24') {
        mainProductMedia.classList.add('size-24', 'has-size-24');
      } else if (foundSize === '26') {
        mainProductMedia.classList.add('size-26', 'has-size-26');
      } else if (foundSize === '28') {
        mainProductMedia.classList.add('size-28', 'has-size-28');
      } else if (foundSize === '30') {
        mainProductMedia.classList.add('size-30', 'has-size-30');
      }
    }
  }

  /**
   * Update classes based on selected neck option (Narrow or Wide)
   */
  updateNeckClasses() {
    // Find the main product media element
    const mainProductMedia = document.querySelector('.main-product__media');
    if (!mainProductMedia) {
      return;
    }

    // Remove all neck classes first
    mainProductMedia.classList.remove('neck-narrow', 'neck-wide', 'has-neck-narrow', 'has-neck-wide');

    // Check for neck option group specifically
    const neckGroup = this.container.querySelector('.custom-option-group[data-option-group="neck"]');

    if (neckGroup) {
      const selectedOption = neckGroup.querySelector('.custom-option.is-selected');
      if (selectedOption && selectedOption.dataset.optionName) {
        const optionName = selectedOption.dataset.optionName;
        // Normalize option name for comparison (case-insensitive)
        const normalizedOptionName = optionName.toLowerCase().trim();

        // Check for Narrow or Wide
        if (normalizedOptionName.includes('narrow')) {
          mainProductMedia.classList.add('neck-narrow', 'has-neck-narrow');
        } else if (normalizedOptionName.includes('wide')) {
          mainProductMedia.classList.add('neck-wide', 'has-neck-wide');
        }
      }
    } else {
      // Fallback: check all option groups if neck group not found
      const allOptionGroups = this.container.querySelectorAll('.custom-option-group');
      for (const group of allOptionGroups) {
        const optionGroupName = group.dataset.optionGroup;
        if (optionGroupName && optionGroupName.toLowerCase().includes('neck')) {
          const selectedOption = group.querySelector('.custom-option.is-selected');
          if (selectedOption && selectedOption.dataset.optionName) {
            const optionName = selectedOption.dataset.optionName;
            const normalizedOptionName = optionName.toLowerCase().trim();
            if (normalizedOptionName.includes('narrow')) {
              mainProductMedia.classList.add('neck-narrow', 'has-neck-narrow');
            } else if (normalizedOptionName.includes('wide')) {
              mainProductMedia.classList.add('neck-wide', 'has-neck-wide');
            }
          }
          break;
        }
      }
    }
  }


  /**
   * Get the current selected variant price
   * @returns {Object} Object with price (formatted) and raw_price (numeric) properties
   */
  getSelectedVariantPrice() {
    return {
      price: this.selectedVariantPrice,
      raw_price: this.selectedVariantRawPrice
    };
  }

  /**
   * Get all variant prices from main and custom product options
   * @returns {Object} Object with all prices, raw prices, and total
   */
  static getAllVariantPrices() {
    const prices = {
      main: [],
      custom: [],
      all: [],
      totalRawPrice: 0,
      totalFormattedPrice: null
    };

    // Get all main-product-options handlers
    const mainProductOptions = document.querySelector('.main-product-options');
    if (mainProductOptions) {
      const mainWrappers = mainProductOptions.querySelectorAll('.custom-options-wrapper');
      mainWrappers.forEach(wrapper => {
        const productId = wrapper.dataset.productId;
        if (productId && window.customOptionsHandlers[productId]) {
          const handler = window.customOptionsHandlers[productId];

          // Calculate prices correctly:
          // - For single selection groups: use the matching variant price (full price for all selected options)
          // - For multiple selection groups: sum all selected option prices (incremental prices)
          let totalProductPrice = 0;

          // Check if there are any single selection groups
          const singleSelectionGroups = wrapper.querySelectorAll('.custom-option-group:not([data-multiple-selection="true"])');
          const multipleSelectionGroups = wrapper.querySelectorAll('.custom-option-group[data-multiple-selection="true"]');

          // For single selection groups, find the matching variant (which has the full price)
          if (singleSelectionGroups.length > 0) {
            const matchingVariant = handler.findMatchingVariant();
            if (matchingVariant && matchingVariant.raw_price) {
              totalProductPrice = matchingVariant.raw_price;
            }
          }

          // For multiple selection groups, add incremental prices
          multipleSelectionGroups.forEach(group => {
            const selectedOptions = group.querySelectorAll('.custom-option.is-selected');
            selectedOptions.forEach(option => {
              const variantId = option.dataset.variantId;
              if (variantId && handler.variantMapping && handler.variantMapping.variants) {
                const variant = handler.variantMapping.variants.find(v => v.id === parseInt(variantId));
                if (variant && variant.raw_price) {
                  totalProductPrice += variant.raw_price;
                }
              }
            });
          });

          if (totalProductPrice > 0) {
            // Format the price for display
            const formattedPrice = (totalProductPrice / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD'
            });

            prices.main.push({
              productId: productId,
              price: formattedPrice,
              raw_price: totalProductPrice
            });
            prices.all.push(totalProductPrice);
          }
        }
      });
    }

    // Get all custom-product-options handlers
    const customProductOptions = document.querySelectorAll('.custom-product-options');
    customProductOptions.forEach(customContainer => {
      const customWrappers = customContainer.querySelectorAll('.custom-options-wrapper');
      customWrappers.forEach(wrapper => {
        const productId = wrapper.dataset.productId;
        if (productId && window.customOptionsHandlers[productId]) {
          const handler = window.customOptionsHandlers[productId];

          // Calculate prices correctly:
          // - For single selection groups: use the matching variant price (full price for all selected options)
          // - For multiple selection groups: sum all selected option prices (incremental prices)
          let totalProductPrice = 0;

          // Check if there are any single selection groups
          const singleSelectionGroups = wrapper.querySelectorAll('.custom-option-group:not([data-multiple-selection="true"])');
          const multipleSelectionGroups = wrapper.querySelectorAll('.custom-option-group[data-multiple-selection="true"]');

          // For single selection groups, find the matching variant (which has the full price)
          if (singleSelectionGroups.length > 0) {
            const matchingVariant = handler.findMatchingVariant();
            if (matchingVariant && matchingVariant.raw_price) {
              totalProductPrice = matchingVariant.raw_price;
            }
          }

          // For multiple selection groups, add incremental prices
          multipleSelectionGroups.forEach(group => {
            const selectedOptions = group.querySelectorAll('.custom-option.is-selected');
            selectedOptions.forEach(option => {
              const variantId = option.dataset.variantId;
              if (variantId && handler.variantMapping && handler.variantMapping.variants) {
                const variant = handler.variantMapping.variants.find(v => v.id === parseInt(variantId));
                if (variant && variant.raw_price) {
                  totalProductPrice += variant.raw_price;
                }
              }
            });
          });

          if (totalProductPrice > 0) {
            // Format the price for display
            const formattedPrice = (totalProductPrice / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD'
            });

            prices.custom.push({
              productId: productId,
              price: formattedPrice,
              raw_price: totalProductPrice
            });
            prices.all.push(totalProductPrice);
          }
        }
      });
    });

    // Get additional accessories prices
    const additionalAccessoriesPrices = CustomOptionsHandler.getAdditionalAccessoriesPrices();
    if (additionalAccessoriesPrices.length > 0) {
      prices.all.push(...additionalAccessoriesPrices);
    }

    // Calculate total
    prices.totalRawPrice = prices.all.reduce((sum, price) => sum + (price || 0), 0);

    // Format total price (assuming prices are in cents)
    if (prices.totalRawPrice > 0) {
      prices.totalFormattedPrice = (prices.totalRawPrice / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    }

    return prices;
  }

  /**
   * Get prices from selected additional accessories
   * @returns {Array<number>} Array of raw prices (in cents) for selected accessories
   */
  static getAdditionalAccessoriesPrices() {
    const prices = [];

    // Get all checked additional accessories checkboxes and radios
    const additionalAccessories = document.querySelector('.additional-accessories');
    if (!additionalAccessories) return prices;

    // Get all checked main checkboxes (for single variant products)
    const checkedMainCheckboxes = additionalAccessories.querySelectorAll('.additional-accessories__checkbox--main:checked');

    checkedMainCheckboxes.forEach(checkbox => {
      const hasVariants = checkbox.dataset.hasVariants === 'true';
      const productId = checkbox.dataset.productId;

      if (!hasVariants) {
        // Single variant product - get price from data-price attribute
        const price = parseInt(checkbox.dataset.price, 10);
        if (price && !isNaN(price)) {
          prices.push(price);
        }
      } else {
        // Multiple variants - get price from selected variant
        const variantsContainer = document.getElementById(`variants-${productId}`);
        if (variantsContainer) {
          const selectedVariant = variantsContainer.querySelector('.additional-accessories__radio:checked');
          if (selectedVariant) {
            const price = parseInt(selectedVariant.dataset.price, 10);
            if (price && !isNaN(price)) {
              prices.push(price);
            }
          }
        }
      }
    });

    return prices;
  }

  /**
   * Get total sum of all variant raw prices
   * @returns {number} Total raw price (in cents)
   */
  static getTotalRawPrice() {
    const prices = CustomOptionsHandler.getAllVariantPrices();
    return prices.totalRawPrice;
  }

  /**
   * Find the lowest price variant for a selected color across all deck lengths
   */
  findLowestPriceVariantForColor(selectedColor) {
    if (!this.variantMapping || !this.variantMapping.variants || !selectedColor) {
      return null;
    }

    const normalizedColor = selectedColor.toLowerCase().trim();
    let lowestPriceVariant = null;
    let lowestPrice = Infinity;

    // Find all variants that match the selected color
    for (const variant of this.variantMapping.variants) {
      if (!variant.options || variant.options.length === 0) continue;

      // Check if this variant has the selected color
      let hasMatchingColor = false;
      for (const variantOption of variant.options) {
        const normalizedVariantOption = String(variantOption).toLowerCase().trim();
        if (normalizedVariantOption === normalizedColor) {
          hasMatchingColor = true;
          break;
        }
      }

      // If variant matches the color, check if it has a lower price
      if (hasMatchingColor && variant.raw_price != null && variant.raw_price !== undefined) {
        const price = parseFloat(variant.raw_price) || 0;
        if (price < lowestPrice && price > 0) {
          lowestPrice = price;
          lowestPriceVariant = variant;
        }
      }
    }

    return lowestPriceVariant;
  }

  /**
   * Update all deck length prices based on selected color
   * Shows "INCLUDED" for the lowest price deck length variant
   */
  updateAllDeckLengthPrices() {
    // Get selected color
    const paintGroup = this.container.querySelector('.custom-option-group[data-option-group="paint"], .custom-option-group[data-option-group="color"], .custom-option-group[data-option-group="colour"]');
    if (!paintGroup) return;

    const selectedColorOption = paintGroup.querySelector('.custom-option.is-selected');
    if (!selectedColorOption) return;

    const selectedColor = selectedColorOption.dataset.optionName;
    if (!selectedColor) return;

    // Find the lowest price variant for this color
    const lowestPriceVariant = this.findLowestPriceVariantForColor(selectedColor);
    if (!lowestPriceVariant) return;

    // Find all deck length option groups
    const deckLengthGroups = this.container.querySelectorAll('.custom-option-group[data-option-group="deck-length"], .custom-option-group[data-option-group="length"]');

    deckLengthGroups.forEach(group => {
      const priceContainer = group.querySelector('.custom-option-group__price');
      if (!priceContainer) return;

      // Get the selected deck length option
      const selectedDeckLengthOption = group.querySelector('.custom-option.is-selected');
      if (!selectedDeckLengthOption) return;

      const selectedDeckLength = selectedDeckLengthOption.dataset.optionName;
      if (!selectedDeckLength) return;

      // Find the variant that matches both the selected deck length and selected color
      if (!this.variantMapping || !this.variantMapping.variants) return;

      const normalizedColor = selectedColor.toLowerCase().trim();
      const normalizedDeckLength = selectedDeckLength.toLowerCase().trim();

      let matchingVariant = null;
      for (const variant of this.variantMapping.variants) {
        if (!variant.options || variant.options.length === 0) continue;

        let hasColor = false;
        let hasDeckLength = false;

        for (const variantOption of variant.options) {
          const normalizedVariantOption = String(variantOption).toLowerCase().trim();
          if (normalizedVariantOption === normalizedColor) {
            hasColor = true;
          }
          if (normalizedVariantOption === normalizedDeckLength) {
            hasDeckLength = true;
          }
        }

        if (hasColor && hasDeckLength) {
          matchingVariant = variant;
          break;
        }
      }

      if (matchingVariant) {
        // Check if this is the lowest price variant
        const isLowestPrice = matchingVariant.id === lowestPriceVariant.id;

        if (isLowestPrice || matchingVariant.raw_price == 0 || !matchingVariant.raw_price) {
          priceContainer.innerHTML = 'INCLUDED';
          priceContainer.classList.add('included');
        } else {
          // Calculate the price difference
          const lowestPrice = parseFloat(lowestPriceVariant.raw_price) || 0;
          const currentPrice = parseFloat(matchingVariant.raw_price) || 0;
          const priceDifference = currentPrice - lowestPrice;

          // Format the difference price
          if (priceDifference > 0 && typeof formatPrice === 'function' && typeof Shopify !== 'undefined' && Shopify.money_format) {
            const formattedDifference = formatPrice(Shopify.money_format, (priceDifference / 100));
            priceContainer.innerHTML = '+ ' + formattedDifference;
          } else {
            // Fallback to variant price if formatPrice is not available
            priceContainer.innerHTML = '+ ' + matchingVariant.price;
          }
          priceContainer.classList.remove('included');
        }
      }
    });
  }

  /**
   * Update price display in custom-option-group__price
   */
  /**
   * Update price for multiple selection groups
   * Calculates total price from all selected options in the group
   */
  updatePriceForMultipleSelection(group) {
    const optionGroup = group.dataset.optionGroup;
    const selectedOptions = group.querySelectorAll('.custom-option.is-selected');
    let totalPrice = 0;
    let totalRawPrice = 0;

    selectedOptions.forEach(selectedOption => {
      let variantId = selectedOption.dataset.variantId;

      // If no variant ID, try to find matching variant
      if (!variantId) {
        const optionName = selectedOption.dataset.optionName;
        const matchingVariant = this.findMatchingVariantForOption(optionGroup, optionName);
        if (matchingVariant && matchingVariant.id) {
          variantId = matchingVariant.id;
          selectedOption.setAttribute('data-variant-id', variantId);
        }
      }

      if (variantId && this.variantMapping && this.variantMapping.variants) {
        const variant = this.variantMapping.variants.find(v => v.id === parseInt(variantId));
        if (variant && variant.raw_price) {
          totalRawPrice += variant.raw_price;
        }
      }
    });

    // Update selection counter
    this.updateSelectionCounter(group);

    // Format the total price
    if (totalRawPrice > 0) {
      totalPrice = this.formatPrice(totalRawPrice);

      // Update price display in the group header
      const priceContainer = group.querySelector('.custom-option-group__price');
      if (priceContainer) {
        priceContainer.textContent = '+ ' + totalPrice;
      }

      // Store prices
      this.selectedVariantPrice = totalPrice;
      this.selectedVariantRawPrice = totalRawPrice;

      // Dispatch price update event
      const priceEvent = new CustomEvent('variantPriceUpdated', {
        detail: {
          price: totalPrice,
          raw_price: totalRawPrice,
          optionGroup: optionGroup
        }
      });
      document.dispatchEvent(priceEvent);
    } else {
      // If no selections, reset the price display to blank
      const priceContainer = group.querySelector('.custom-option-group__price');
      if (priceContainer) {
        priceContainer.textContent = '';
      }

      // Reset prices
      this.selectedVariantPrice = null;
      this.selectedVariantRawPrice = null;
    }

    // Trigger total price update
    if (typeof triggerPriceUpdate === 'function') {
      triggerPriceUpdate();
    } else {
      // Fallback: dispatch total price update event
      const totalPriceData = CustomOptionsHandler.getAllVariantPrices();
      const totalPriceEvent = new CustomEvent('totalVariantPriceUpdated', {
        detail: totalPriceData
      });
      document.dispatchEvent(totalPriceEvent);
    }
  }

  /**
   * Update selection counter for multiple selection groups
   */
  updateSelectionCounter(group) {
    const counterElement = group.querySelector('.custom-option-group__counter');
    if (!counterElement) return;

    const allOptions = group.querySelectorAll('.custom-option');
    const selectedOptions = group.querySelectorAll('.custom-option.is-selected');
    
    const totalCount = allOptions.length;
    const selectedCount = selectedOptions.length;
    
    counterElement.textContent = `Selected ${selectedCount}/${totalCount}`;
  }

  /**
   * Format price from raw price (cents) to formatted string
   */
  formatPrice(rawPrice) {
    // Convert cents to dollars
    const dollars = rawPrice / 100;
    // Format with currency symbol and 2 decimal places
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(dollars);
  }

  updateCustomOptionsPrice(variant) {
    if (!variant) {
      console.warn('updateCustomOptionsPrice: No variant provided');
      return;
    }

    // Store the current variant price
    this.selectedVariantPrice = variant.price || null;
    this.selectedVariantRawPrice = variant.raw_price || null;

    // Dispatch custom event with price information
    const priceEvent = new CustomEvent('variantPriceUpdated', {
      detail: {
        price: this.selectedVariantPrice,
        raw_price: this.selectedVariantRawPrice,
        variant: variant
      }
    });
    this.container.dispatchEvent(priceEvent);

    // Dispatch global event for total price update
    const totalPriceData = CustomOptionsHandler.getAllVariantPrices();
    const totalPriceEvent = new CustomEvent('totalVariantPriceUpdated', {
      detail: totalPriceData
    });
    document.dispatchEvent(totalPriceEvent);

    // Find all price containers in custom option groups
    const priceContainers = this.container.querySelectorAll('.custom-option-group__price');
    const extra_priceContainers = this.container.querySelectorAll('.custom-option-group__extra_price');
    if (priceContainers.length === 0 && extra_priceContainers.length === 0) {
      console.warn('updateCustomOptionsPrice: No price containers found');
      return;
    }

    // Validate price exists
    if (!variant.price) {
      console.warn('updateCustomOptionsPrice: No valid price found for variant', variant.price);
      return;
    }


    // Update all extra_price containers (for color options)
    extra_priceContainers.forEach(extra_price => {
      // Find the option group this extra_price belongs to
      const optionGroup = extra_price.closest('.custom-option-group');
      if (optionGroup) {
        // Find the selected option in this paint/color group
        const selectedOption = optionGroup.querySelector('.custom-option.is-selected');
        let paintColorVariant = null;

        if (selectedOption && selectedOption.dataset.variantId) {
          // Get variant by ID from the selected option
          const variantId = parseInt(selectedOption.dataset.variantId);
          if (this.variantMapping && this.variantMapping.variants) {
            paintColorVariant = this.variantMapping.variants.find(v => v.id === variantId);
          }
        }

        // If no variant found from selected option, try to find by option group name
        if (!paintColorVariant) {
          const optionGroupName = optionGroup.dataset.optionGroup;
          paintColorVariant = this.findVariantForOption(optionGroupName);
        }

        // Use paint/color specific variant if found, otherwise fall back to overall variant
        const variantToUse = paintColorVariant || variant;

        // Check if this is a two-tone color option
        const colorName = selectedOption ? (selectedOption.dataset.optionName || '') : '';
        const isTwoToneCustom = colorName.toLowerCase().includes('two-tone custom') || colorName.toLowerCase().includes('two tone custom');

        // For two-tone colors, show color_text (keep current behavior)
        if (isTwoToneCustom && variantToUse) {
          extra_price.innerHTML = variantToUse.color_text || '';
          extra_price.classList.remove('included');
        }
        // For normal colors, always show "INCLUDED" (no price logic applied)
        else {
          extra_price.innerHTML = 'INCLUDED';
          extra_price.classList.add('included');
        }
      }
    });

    // Get selected color to find lowest price variant
    const paintGroup = this.container.querySelector('.custom-option-group[data-option-group="paint"], .custom-option-group[data-option-group="color"], .custom-option-group[data-option-group="colour"]');
    let selectedColor = null;
    let lowestPriceVariant = null;

    if (paintGroup) {
      const selectedColorOption = paintGroup.querySelector('.custom-option.is-selected');
      if (selectedColorOption) {
        selectedColor = selectedColorOption.dataset.optionName;
        // Find the lowest price variant for this color
        lowestPriceVariant = this.findLowestPriceVariantForColor(selectedColor);
      }
    }

    priceContainers.forEach(container => {
      // Find which option group this price container belongs to
      const optionGroup = container.closest('.custom-option-group');
      const isDeckLengthGroup = optionGroup && (
        optionGroup.dataset.optionGroup === 'deck-length' ||
        optionGroup.dataset.optionGroup === 'length' ||
        optionGroup.dataset.optionGroup?.toLowerCase().includes('deck') ||
        optionGroup.dataset.optionGroup?.toLowerCase().includes('length')
      );

      // Check if this is the lowest price variant for the selected color (only for deck length groups)
      const isLowestPrice = isDeckLengthGroup && lowestPriceVariant && variant.id === lowestPriceVariant.id;

      if (variant.raw_price != 0) {
        // If this is the lowest price variant for deck length, show "INCLUDED"
        if (isLowestPrice) {
          container.innerHTML = 'INCLUDED';
          container.classList.add('included');
        } else if (isDeckLengthGroup && lowestPriceVariant) {
          // Calculate the price difference for deck length groups
          const lowestPrice = parseFloat(lowestPriceVariant.raw_price) || 0;
          const currentPrice = parseFloat(variant.raw_price) || 0;
          const priceDifference = currentPrice - lowestPrice;

          // Format the difference price
          if (priceDifference > 0 && typeof formatPrice === 'function' && typeof Shopify !== 'undefined' && Shopify.money_format) {
            const formattedDifference = formatPrice(Shopify.money_format, (priceDifference / 100));
            container.innerHTML = '+ ' + formattedDifference;
          } else {
            // Fallback to variant price if formatPrice is not available
            container.innerHTML = '+ ' + variant.price;
          }
          container.classList.remove('included');
        } else {
          container.innerHTML = '+ ' + variant.price;
          container.classList.remove('included');
        }
      } else {
        container.innerHTML = 'INCLUDED';
        container.classList.add('included');
      }
    });

  }

  updateProductImage() {
    // Find the variant that matches selected options
    const matchingVariant = this.findMatchingVariant();

    // Update image-change elements for length, neck, and wheel with their specific variants
    // Update length image
    const lengthVariant = this.findVariantForOption('length');
    if (lengthVariant && lengthVariant.featured_media) {
      const lengthImage = document.querySelector('.image-change.length');
      if (lengthImage) {
        lengthImage.src = lengthVariant.featured_media;
      }
    }

    // Update neck image
    const neckVariant = this.findVariantForOption('neck');
    if (neckVariant && neckVariant.featured_media) {
      const neckImage = document.querySelector('.image-change.neck');
      if (neckImage) {
        neckImage.src = neckVariant.featured_media;
      }
    }

    // Update wheel image
    const wheelVariant = this.findVariantForOption('wheel');
    if (wheelVariant && wheelVariant.featured_media) {
      const wheelImage = document.querySelector('.image-change.wheel');
      if (wheelImage) {
        wheelImage.src = wheelVariant.featured_media;
      }
    }
  }

  /**
   * Find matching variant for a specific option in a group
   * Used for multiple selection to get variant prices
   */
  findMatchingVariantForOption(optionGroup, optionName) {
    if (!this.variantMapping || !this.variantMapping.variants) {
      return null;
    }

    const normalizedOptionName = String(optionName).toLowerCase().trim();

    // Try to find variant that matches this option
    for (const variant of this.variantMapping.variants) {
      if (!variant.options || variant.options.length === 0) continue;

      for (const variantOption of variant.options) {
        const normalizedVariantOption = String(variantOption).toLowerCase().trim();
        if (normalizedVariantOption === normalizedOptionName) {
          return variant;
        }
      }
    }

    return null;
  }

  /**
   * Find a variant that matches a specific option type (length, neck, wheel)
   * Uses current selected options but prioritizes the specified option type
   */
  findVariantForOption(optionType) {
    if (!this.variantMapping || !this.variantMapping.variants) {
      return null;
    }

    // Normalize option type for lookup (handleize)
    const normalizedOptionType = optionType.toLowerCase().trim();

    // Try to find the selected value - check both exact match and normalized match
    let selectedValue = this.selectedOptions[optionType] || this.selectedOptions[normalizedOptionType];

    // If still not found, check all keys with normalized comparison
    if (!selectedValue) {
      for (const key in this.selectedOptions) {
        if (key.toLowerCase().trim() === normalizedOptionType) {
          selectedValue = this.selectedOptions[key];
          break;
        }
      }
    }

    if (!selectedValue) {
      console.warn(`findVariantForOption: No selected value found for ${optionType}`, this.selectedOptions);
      return null;
    }

    const normalizedSelectedValue = selectedValue.toLowerCase().trim();
    let bestMatch = null;
    let bestMatchScore = 0;

    // Try to find a variant that matches this specific option
    // Priority: exact match for the option type, then best match with other options
    for (const variant of this.variantMapping.variants) {
      if (!variant.options || variant.options.length === 0 || !variant.featured_media) {
        continue;
      }

      // Check if this variant has the selected option value for the specific type
      let hasMatchingOption = false;
      for (const variantOption of variant.options) {
        const normalizedVariantOption = String(variantOption).toLowerCase().trim();
        if (normalizedVariantOption === normalizedSelectedValue) {
          hasMatchingOption = true;
          break;
        }
      }

      if (hasMatchingOption) {
        // Count how many other selected options also match this variant
        let matchScore = 1; // Start with 1 for the required option match

        for (const optionGroup in this.selectedOptions) {
          // Skip if this is the option type we're looking for
          const normalizedOptionGroup = optionGroup.toLowerCase().trim();
          if (normalizedOptionGroup === normalizedOptionType) continue; // Already counted

          const selectedOption = this.selectedOptions[optionGroup];
          const isArray = Array.isArray(selectedOption);
          const selectedOptionsList = isArray ? selectedOption : [selectedOption];

          // Check if any selected option matches
          let groupMatched = false;
          for (const selectedOptionName of selectedOptionsList) {
            const normalizedSelected = String(selectedOptionName).toLowerCase().trim();
            for (const variantOption of variant.options) {
              const normalizedVariantOption = String(variantOption).toLowerCase().trim();
              if (normalizedVariantOption === normalizedSelected) {
                matchScore++;
                groupMatched = true;
                break;
              }
            }
            // If we found a match for this group, no need to check other options in this group
            if (groupMatched) break;
          }
        }

        // Keep track of the best match (most matching options)
        if (!bestMatch || matchScore > bestMatchScore) {
          bestMatch = variant;
          bestMatchScore = matchScore;
        }
      }
    }

    // Return the best match if found, otherwise return null
    return bestMatch;
  }

  findMatchingVariant() {
    if (!this.variantMapping || !this.variantMapping.variants) {
      console.warn('findMatchingVariant: No variant mapping available');
      return null;
    }

    // Try to find variant by matching option names
    for (const variant of this.variantMapping.variants) {
      if (!variant.options || variant.options.length === 0) continue;

      let matches = true;
      let matchedOptions = [];

      for (const optionGroup in this.selectedOptions) {
        const selectedOption = this.selectedOptions[optionGroup];
        const isArray = Array.isArray(selectedOption);

        // Handle both single selection (string) and multiple selection (array)
        const selectedOptionsList = isArray ? selectedOption : [selectedOption];
        let optionFound = false;

        // For multiple selection, check if any selected option matches
        // For single selection, check if the selected option matches
        for (const selectedOptionName of selectedOptionsList) {
          const normalizedSelected = String(selectedOptionName).toLowerCase().trim();

          for (const variantOption of variant.options) {
            const normalizedVariantOption = String(variantOption).toLowerCase().trim();
            if (normalizedVariantOption === normalizedSelected) {
              optionFound = true;
              matchedOptions.push(variantOption);
              break;
            }
          }

          if (optionFound) break;
        }

        if (!optionFound) {
          matches = false;
          break;
        }
      }

      if (matches) {
        return variant;
      }
    }

    // Fallback: Try to find by variant ID from data attributes
    const selectedOption = this.container.querySelector('.custom-option.is-selected[data-variant-id]');
    if (selectedOption) {
      const variantId = parseInt(selectedOption.dataset.variantId);
      const variant = this.variantMapping.variants.find(v => v.id === variantId);
      if (variant) {
        return variant;
      }
    }

    console.warn('findMatchingVariant: No matching variant found');
    return null;
  }

}

// Store instances globally for jQuery access
window.customOptionsHandlers = {};

// Global function to get all variant prices and total
window.getAllVariantPrices = function () {
  return CustomOptionsHandler.getAllVariantPrices();
};

// Global function to get total raw price
window.getTotalVariantRawPrice = function () {
  return CustomOptionsHandler.getTotalRawPrice();
};

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.custom-options-wrapper').forEach(container => {
    const handler = new CustomOptionsHandler(container);
    // Store instance by product ID for easy access
    if (handler.productId) {
      window.customOptionsHandlers[handler.productId] = handler;
    }
    // Also store by section ID as fallback
    if (handler.sectionId) {
      window.customOptionsHandlers[handler.sectionId] = handler;
    }
  });

  // Update payment type visibility on page load
  updatePaymentTypeVisibility();
  
  // If finance is selected on page load, calculate finance price with 10% due at delivery
  const selectedFinanceOption = document.querySelector('.custom-option.is-selected[data-option-id="finance"]');
  if (selectedFinanceOption && typeof window.calculateFinance === 'function') {
    setTimeout(() => {
      window.calculateFinance();
    }, 200);
  }

  // Listen for total price updates and update payment type visibility
  document.addEventListener('totalVariantPriceUpdated', (event) => {
    updatePaymentTypeVisibility();
    updateMainProductPrice(event.detail);
    
    // If finance is selected, recalculate finance price (including due at delivery)
    const selectedFinanceOption = document.querySelector('.custom-option.is-selected[data-option-id="finance"]');
    if (selectedFinanceOption && typeof window.calculateFinance === 'function') {
      setTimeout(() => {
        window.calculateFinance();
      }, 100);
    }
  });

  // Listen for clicks on payment options
  document.addEventListener('click', (event) => {
    const option = event.target.closest('.custom-option[data-option-id="cash"], .custom-option[data-option-id="finance"]');
    if (option) {
      // Small delay to ensure the option is selected first
      setTimeout(() => {
        updatePaymentTypeVisibility();
      }, 10);
    }
  });
});

/**
 * Update main product price display
 */
function updateMainProductPrice(priceData) {
  if (!priceData || priceData.totalRawPrice === undefined || priceData.totalRawPrice === null) return;

  // Find price element by section ID
  const productInfo = document.querySelector('product-info');
  let priceElement = null;

  if (productInfo) {
    const sectionId = productInfo.dataset?.section || productInfo.dataset?.originalSection;
    if (sectionId) {
      priceElement = document.getElementById(`price-${sectionId}`);
    }
  }

  // Fallback: try to find by other selectors
  if (!priceElement) {
    priceElement = document.querySelector('#price-main') ||
      document.querySelector('[id^="price-"]') ||
      document.querySelector('.product__price');
  }

  if (!priceElement) return;

  // Find the price container inside
  const priceContainer = priceElement.querySelector('.price__container');
  if (!priceContainer) return;

  // Get money format
  const moneyFormat = priceContainer.dataset?.moneyFormat;
  let formattedPrice = '';

  // Format the price
  if (moneyFormat && typeof formatPrice === 'function' && typeof Shopify !== 'undefined') {
    formattedPrice = formatPrice(moneyFormat, priceData.totalRawPrice / 100);
  } else {
    formattedPrice = priceData.totalFormattedPrice || (priceData.totalRawPrice / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  // Find or create price structure
  let priceSale = priceContainer.querySelector('.price__sale');
  let priceRegular = priceContainer.querySelector('.price__regular');

  // Remove price__regular if it exists (we'll use price__sale)
  if (priceRegular) {
    priceRegular.remove();
  }

  // Create price__sale if it doesn't exist
  if (!priceSale) {
    priceSale = document.createElement('div');
    priceSale.className = 'price__sale';
    priceContainer.appendChild(priceSale);
  }

  // Create price__sale-inner if it doesn't exist
  let priceSaleInner = priceSale.querySelector('.price__sale-inner');
  if (!priceSaleInner) {
    priceSaleInner = document.createElement('div');
    priceSaleInner.className = 'price__sale-inner';
    priceSale.appendChild(priceSaleInner);
  }

  // Update the price text
  const priceText = priceSaleInner.querySelector('ins') || priceSaleInner.querySelector('span:not(.visually-hidden)');
  if (priceText) {
    // Update existing price element
    if (priceText.tagName === 'INS') {
      const innerIns = priceText.querySelector('ins');
      if (innerIns) {
        innerIns.textContent = formattedPrice;
      } else {
        priceText.innerHTML = `<span class="visually-hidden">${window.variantStrings?.regularPrice || 'Regular price'}</span>${formattedPrice}`;
      }
    } else {
      priceText.textContent = formattedPrice;
    }
  } else {
    // Create new price structure
    priceSaleInner.innerHTML = `
      <span class="visually-hidden">${window.variantStrings?.regularPrice || 'Regular price'}</span>
      <ins>
        <span class="visually-hidden">${window.variantStrings?.priceSale || 'Sale price'}</span>
        ${formattedPrice}
      </ins>
    `;
  }
}

/**
 * Update payment type visibility based on data-option-id
 */
function updatePaymentTypeVisibility() {
  // Find selected payment option by data-option-id
  const selectedCashOption = document.querySelector('.custom-option.is-selected[data-option-id="cash"]');
  const selectedFinanceOption = document.querySelector('.custom-option.is-selected[data-option-id="finance"]');

  const cashPriceWrappers = document.querySelectorAll('.cash_price_wrapper');
  const financePriceWrappers = document.querySelectorAll('.finance_price_wrapper');
  const cashPriceElements = document.querySelectorAll('.cash_price_wrapper .price');
  const financePriceElements = document.querySelectorAll('.finance_price_wrapper .price');

  const totalRawPrice = window.getTotalVariantRawPrice();

  if (selectedCashOption) {
    // Show cash, hide finance
    cashPriceWrappers.forEach(element => {
      element.style.display = 'block';
    });
    financePriceWrappers.forEach(element => {
      element.style.display = 'none';
    });

    // Update cash price
    if (cashPriceElements.length > 0 && totalRawPrice !== null && totalRawPrice !== undefined && totalRawPrice > 0) {
      const formattedPrice = formatPrice(Shopify.money_format, (totalRawPrice / 100));
      cashPriceElements.forEach(element => {
        element.textContent = formattedPrice;
      });
    }
  } else if (selectedFinanceOption) {
    // Hide cash, show finance
    cashPriceWrappers.forEach(element => {
      element.style.display = 'none';
    });
    financePriceWrappers.forEach(element => {
      element.style.display = 'block';
    });

    // Trigger finance calculation in modal if it exists
    if (typeof window.calculateFinance === 'function') {
      // Finance calculation will update the price wrapper with due at delivery included
      setTimeout(() => {
        window.calculateFinance();
      }, 100);
    }
  }
}

/**
 * Update .cash_price_wrapper .price with total raw price
 */
function updateCashPriceWrapper() {
  updatePaymentTypeVisibility();
}

// Re-initialize on section load (for theme editor)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.custom-options-wrapper');
    if (container) {
      const handler = new CustomOptionsHandler(container);
      // Store instance for jQuery access
      if (handler.productId) {
        window.customOptionsHandlers[handler.productId] = handler;
      }
      if (handler.sectionId) {
        window.customOptionsHandlers[handler.sectionId] = handler;
      }
    }
  });
}

/**
 * Additional Accessories Handler
 * Shows/hides variants when main product checkbox is checked
 */
(function () {
  // Function to trigger price update event
  function triggerPriceUpdate() {
    const totalPriceData = CustomOptionsHandler.getAllVariantPrices();
    const totalPriceEvent = new CustomEvent('totalVariantPriceUpdated', {
      detail: totalPriceData
    });
    document.dispatchEvent(totalPriceEvent);
  }

  function initAdditionalAccessories() {
    const mainCheckboxes = document.querySelectorAll('.additional-accessories__checkbox--main');

    mainCheckboxes.forEach(checkbox => {
      const isRequired = checkbox.dataset.isRequired === 'true';
      const hasVariants = checkbox.dataset.hasVariants === 'true';

      // Prevent unchecking required accessories
      if (isRequired) {
        checkbox.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          checkbox.checked = true;
          return false;
        });

        // Also prevent via change event
        checkbox.addEventListener('change', function (e) {
          if (!checkbox.checked) {
            checkbox.checked = true;
          }
        });
      }

      if (hasVariants) {
        const productId = checkbox.dataset.productId;
        const variantsContainer = document.getElementById(`variants-${productId}`);

        if (variantsContainer) {
          // Get main price element for updates
          const mainCheckboxLabel = checkbox.closest('.additional-accessories__item--main').querySelector('.additional-accessories__label');
          const mainPriceElement = mainCheckboxLabel ? mainCheckboxLabel.querySelector('.additional-accessories__price') : null;

          // Show/hide variants based on checkbox state
          const toggleVariants = () => {
            // If required, always show variants and keep checkbox checked
            if (isRequired) {
              checkbox.checked = true;
              variantsContainer.style.display = 'block';
            } else if (checkbox.checked) {
              variantsContainer.style.display = 'block';
            } else {
              variantsContainer.style.display = 'none';
              // Uncheck all variant radio buttons when main product is unchecked
              const variantRadios = variantsContainer.querySelectorAll('.additional-accessories__radio');
              variantRadios.forEach(radio => {
                if (radio.dataset.isRequired !== 'true') {
                  radio.checked = false;
                }
              });

              // Trigger price update event
              triggerPriceUpdate();
              return;
            }

            // Always select first variant if none is selected
            let selectedVariant = variantsContainer.querySelector('.additional-accessories__radio:checked');
            if (!selectedVariant) {
              selectedVariant = variantsContainer.querySelector('.additional-accessories__radio');
              if (selectedVariant) {
                selectedVariant.checked = true;
                // Update main checkbox value and data attributes
                checkbox.value = selectedVariant.value;
                checkbox.dataset.variantId = selectedVariant.dataset.variantId;
                checkbox.dataset.price = selectedVariant.dataset.price;
              }
            }

            // Update price to match selected variant
            if (selectedVariant && mainPriceElement) {
              const variantLabel = selectedVariant.closest('.additional-accessories__item--variant').querySelector('.additional-accessories__label');
              const variantPriceElement = variantLabel ? variantLabel.querySelector('.additional-accessories__price') : null;
              if (variantPriceElement) {
                mainPriceElement.textContent = variantPriceElement.textContent;
              }
            }

            // Trigger price update event
            triggerPriceUpdate();
          };

          // Update main checkbox when variant is selected
          const variantRadios = variantsContainer.querySelectorAll('.additional-accessories__radio');

          variantRadios.forEach(radio => {
            const variantIsRequired = radio.dataset.isRequired === 'true';

            // Prevent unchecking required variant radios
            if (variantIsRequired) {
              radio.addEventListener('click', function (e) {
                if (!radio.checked) {
                  e.preventDefault();
                  e.stopPropagation();
                  radio.checked = true;
                  return false;
                }
              });
            }

            radio.addEventListener('change', () => {
              if (radio.checked) {
                // Update main checkbox value and data attributes
                checkbox.value = radio.value;
                checkbox.dataset.variantId = radio.dataset.variantId;
                checkbox.dataset.price = radio.dataset.price;
                // Ensure main checkbox is checked
                checkbox.checked = true;

                // Update the displayed price
                if (mainPriceElement) {
                  const variantLabel = radio.closest('.additional-accessories__item--variant').querySelector('.additional-accessories__label');
                  const variantPriceElement = variantLabel ? variantLabel.querySelector('.additional-accessories__price') : null;
                  if (variantPriceElement) {
                    mainPriceElement.textContent = variantPriceElement.textContent;
                  }
                }

                // Trigger price update event
                triggerPriceUpdate();
              }
            });
          });

          // Set initial state - this will select first variant if checkbox is checked
          toggleVariants();

          // Listen for changes (only if not required, as required ones are disabled)
          if (!isRequired) {
            checkbox.addEventListener('change', toggleVariants);
          }
        }
      } else {
        // Single variant product - add price update listener
        if (!isRequired) {
          checkbox.addEventListener('change', function () {
            triggerPriceUpdate();
          });
        } else {
          // For required single variant products, ensure they're always checked and trigger initial price update
          checkbox.checked = true;
          triggerPriceUpdate();
        }
      }
    });

    // Also listen for all additional accessories checkboxes/radios changes
    const additionalAccessories = document.querySelector('.additional-accessories');
    if (additionalAccessories) {
      additionalAccessories.addEventListener('change', function (e) {
        if (e.target.classList.contains('additional-accessories__checkbox') ||
          e.target.classList.contains('additional-accessories__radio')) {
          triggerPriceUpdate();
        }
      });
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAdditionalAccessories);
  } else {
    initAdditionalAccessories();
  }

  // Re-initialize on section load (for theme editor)
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', initAdditionalAccessories);
  }
})();


